import type { RefinementCtx } from 'zod';
import { z } from 'zod';
import { isAdSetFlowEmpty } from '../utils/adSetFlowCache';

export const AD_SETS_STEP_INDEX = 3;

export const selectedAdSetsFlowsSchema = z
  .record(
    z.string(),
    z
      .string()
      .nullish()
      .transform((flow) => flow ?? ''),
  )
  .default({});

type AdSetsStepValidationContext = Pick<RefinementCtx, 'addIssue'>;

interface AdSetsStepValidationData {
  currentStep?: number;
  selectedAdSets?: unknown[];
  selectedAdSetsFlows?: Record<string, string>;
  selectedAdSetsBudgets?: Record<string, string>;
  selectedAdSetsDuplicatesCount?: Record<string, string>;
}

const getAdSetId = (adSet: object): string | undefined => {
  const idValue = 'id' in adSet ? Reflect.get(adSet, 'id') : undefined;
  return typeof idValue === 'string' && idValue.length > 0 ? idValue : undefined;
};

const getAdSetFlow = (adSet: object): string | null | undefined => {
  const flowValue = 'flow' in adSet ? Reflect.get(adSet, 'flow') : undefined;
  if (typeof flowValue === 'string' || flowValue === null || flowValue === undefined) {
    return flowValue;
  }
  return undefined;
};

const getAdSetFlowAssignedByUser = (adSet: object): boolean =>
  Boolean(
    'flowAssignedByUser' in adSet && Reflect.get(adSet, 'flowAssignedByUser'),
  );

export const adSetRequiresFlowSelect = (adSet: object): boolean => {
  const flow = getAdSetFlow(adSet);
  const flowAssignedByUser = getAdSetFlowAssignedByUser(adSet);
  return isAdSetFlowEmpty(flow) || flowAssignedByUser;
};

export const validateSelectedAdSetsFlows = (
  data: Pick<AdSetsStepValidationData, 'selectedAdSets' | 'selectedAdSetsFlows'>,
  ctx: AdSetsStepValidationContext,
): void => {
  const selectedAdSets = data.selectedAdSets ?? [];
  const selectedAdSetsFlows = data.selectedAdSetsFlows ?? {};

  selectedAdSets.forEach((adSet) => {
    if (!adSet || typeof adSet !== 'object') {
      return;
    }

    const adSetId = getAdSetId(adSet);
    if (!adSetId) {
      return;
    }

    if (!adSetRequiresFlowSelect(adSet)) {
      return;
    }

    if (isAdSetFlowEmpty(selectedAdSetsFlows[adSetId])) {
      ctx.addIssue({
        code: 'custom',
        message: "Can't be empty",
        path: ['selectedAdSetsFlows', adSetId],
      });
    }
  });
};

const validateNonEmptyRecordValues = (
  values: Record<string, string> | undefined,
  fieldName: string,
  ctx: AdSetsStepValidationContext,
  options?: { max?: number },
): void => {
  Object.entries(values ?? {}).forEach(([id, value]) => {
    if (!value?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Cannot be empty',
        path: [fieldName, id],
      });
      return;
    }

    if (options?.max != null) {
      const num = Number(value);
      if (Number.isNaN(num) || num > options.max) {
        ctx.addIssue({
          code: 'custom',
          message: `Cannot be more than ${String(options.max)}`,
          path: [fieldName, id],
        });
      }
    }
  });
};

export const validateAdSetsStep = (
  data: AdSetsStepValidationData,
  ctx: AdSetsStepValidationContext,
  options?: {
    validateBudgets?: boolean;
    validateDuplicates?: boolean;
    duplicatesMax?: number;
  },
): void => {
  if (data.currentStep !== AD_SETS_STEP_INDEX) {
    return;
  }

  if (!data.selectedAdSets?.length) {
    ctx.addIssue({
      code: 'custom',
      message: 'At least one ad set must be selected at this step',
      path: ['selectedAdSets'],
    });
    return;
  }

  validateSelectedAdSetsFlows(data, ctx);

  if (options?.validateBudgets !== false) {
    validateNonEmptyRecordValues(data.selectedAdSetsBudgets, 'selectedAdSetsBudgets', ctx);
  }

  if (options?.validateDuplicates) {
    validateNonEmptyRecordValues(
      data.selectedAdSetsDuplicatesCount,
      'selectedAdSetsDuplicatesCount',
      ctx,
      options.duplicatesMax != null ? { max: options.duplicatesMax } : undefined,
    );
  }
};
